import React, { useState, useMemo } from 'react';
import {
  Input,
  Button,
  Drawer,
  toast,
  useOverlayState,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';

export interface ChangePasswordDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 修改密码抽屉组件
 *
 * 特性：
 * - ✅ 原密码输入
 * - ✅ 新密码输入
 * - ✅ 实时密码强度校验（显示校验要求）
 * - ✅ 前端校验通过后提交后端
 */
export function ChangePasswordDrawer({
  open,
  onOpenChange,
}: ChangePasswordDrawerProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const drawer = useOverlayState({
    isOpen: open,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) {
        onOpenChange(false);
      }
    },
  });

  const changePasswordMutation = trpc.auth.changePassword.useMutation();

  const passwordValidation = useMemo(() => {
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[@*!$%^&*()_+\-=\[\]{}|;:,.<>?/~`]/.test(newPassword);
    const hasMinLength = newPassword.length >= 6;

    return {
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      hasMinLength,
      isValid: hasUppercase && hasLowercase && hasNumber && hasSpecialChar && hasMinLength,
    };
  }, [newPassword]);

  const handleConfirm = async () => {
    if (!currentPassword.trim()) {
      toast.danger('请输入原密码');
      return;
    }

    if (!passwordValidation.isValid) {
      toast.danger('新密码不符合要求，请检查');
      return;
    }

    let loadingKey: string | undefined;
    try {
      loadingKey = toast('正在修改密码…', { isLoading: true, timeout: 0 });

      await changePasswordMutation.mutateAsync({
        currentPassword: currentPassword.trim(),
        newPassword: newPassword.trim(),
      });

      toast.close(loadingKey);
      toast.success('密码修改成功');

      setCurrentPassword('');
      setNewPassword('');

      onOpenChange(false);
    } catch (error) {
      console.error('修改密码失败:', error);
      if (loadingKey !== undefined) {
        toast.close(loadingKey);
      }
      toast.danger(error instanceof Error ? error.message : '修改密码失败');
    }
  };

  const handleCancel = () => {
    setCurrentPassword('');
    setNewPassword('');
    onOpenChange(false);
  };

  return (
    <Drawer state={drawer}>
      <Drawer.Trigger className="sr-only">修改密码</Drawer.Trigger>
      <Drawer.Backdrop>
        <Drawer.Content placement="bottom">
          <Drawer.Dialog className="rounded-t-2xl bg-background pb-8 text-foreground">
            <Drawer.Handle />
            <Drawer.Header className="relative flex h-12 shrink-0 items-center border-b border-divider px-4">
              <Button
                variant="ghost"
                size="sm"
                onPress={handleCancel}
                className="absolute left-4 h-8 px-3"
              >
                取消
              </Button>
              <Drawer.Heading className="flex-1 text-center text-base font-semibold">
                修改密码
              </Drawer.Heading>
              <Button
                variant="primary"
                size="sm"
                onPress={handleConfirm}
                isDisabled={changePasswordMutation.isPending}
                className="absolute right-4 h-8 px-3"
              >
                确定
              </Button>
            </Drawer.Header>
            <Drawer.Body className="px-0">
              <div className="flex flex-col gap-4 px-4 pt-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm text-foreground">原密码</label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="请输入原密码"
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm text-foreground">新密码</label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="请输入新密码"
                    className="w-full"
                  />
                </div>

                {newPassword ? (
                  <div className="flex flex-col gap-2 pt-2">
                    <h3 className="text-sm font-medium text-foreground">新密码要求</h3>
                    <div className="flex flex-col gap-1.5 pl-2">
                      <div
                        className={`text-sm ${passwordValidation.hasUppercase ? 'text-success-600' : 'text-default-500'}`}
                      >
                        {passwordValidation.hasUppercase ? '✓' : '>'} 包含大写字母
                      </div>
                      <div
                        className={`text-sm ${passwordValidation.hasLowercase ? 'text-success-600' : 'text-default-500'}`}
                      >
                        {passwordValidation.hasLowercase ? '✓' : '>'} 包含小写字母
                      </div>
                      <div
                        className={`text-sm ${passwordValidation.hasNumber ? 'text-success-600' : 'text-default-500'}`}
                      >
                        {passwordValidation.hasNumber ? '✓' : '>'} 包含数字
                      </div>
                      <div
                        className={`text-sm ${passwordValidation.hasSpecialChar ? 'text-success-600' : 'text-default-500'}`}
                      >
                        {passwordValidation.hasSpecialChar ? '✓' : '>'} 至少包含一个特殊符号,如@*!等
                      </div>
                      <div
                        className={`text-sm ${passwordValidation.hasMinLength ? 'text-success-600' : 'text-default-500'}`}
                      >
                        {passwordValidation.hasMinLength ? '✓' : '>'} 不少于6位
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </Drawer.Body>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
